import * as React from 'react'

import { Section } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import i18n from 'modules/core/lib/i18n'

import useDayConfigurations from 'modules/work_schedules/hooks/useDayConfigurations'
import WorkScheduleNameForm from 'modules/work_schedules/lib/forms/WorkScheduleNameForm'
import WorkSchedulePeriodForm from 'modules/work_schedules/lib/forms/WorkSchedulePeriodForm'
import type DayConfiguration from 'modules/work_schedules/store/models/DayConfiguration'
import type OverlapPeriod from 'modules/work_schedules/store/models/OverlapPeriod'
import type WorkSchedule from 'modules/work_schedules/store/models/Schedule'

import GeneralInfoSection from './GeneralInfoSection'
import WorkScheduleForm from './WorkScheduleFormComponent'

export interface Props {
  workSchedule: WorkSchedule
  defaultPeriod: OverlapPeriod
  dayConfigurations: DayConfiguration[]
  canAssignEmployees: boolean
  canReassignAll: boolean
  canAddBreaks: boolean
}

const EditWorkScheduleSection = ({
  workSchedule,
  defaultPeriod,
  dayConfigurations,
  canReassignAll,
  canAssignEmployees,
  canAddBreaks,
}: Props) => {
  const [openWarning, setOpenWarning] = React.useState(false)

  const form = useLazyRef(() => new WorkScheduleNameForm(workSchedule.toJS())).current

  const periodFormRef = useLazyRef(() => new WorkSchedulePeriodForm(defaultPeriod.toJS())).current

  const {
    dayConfigForms,
    totalHours,
    onAddDayConfig,
    onDeleteDayConfig,
    onCopyFirstToAll,
    onAddBreak,
    onDeleteBreak,
    saveDayConfigurations,
  } = useDayConfigurations(dayConfigurations, defaultPeriod.attributes.schedule_type)

  const noOfEmployees = workSchedule.employeeIds.length

  const canEdit = !noOfEmployees || canReassignAll

  const { running, performAction } = useAsyncAction(async () => {
    await form.save(workSchedule)
    saveDayConfigurations(+defaultPeriod.attributes.id)

    setOpenWarning(false)
  })

  const onConfirm = () => {
    if (!canEdit) {
      return
    }

    performAction()
  }

  return (
    <>
      <GeneralInfoSection formRef={form} periodFormRef={periodFormRef} />
      <Section.TwoThirds
        illustration="calendar-clock"
        title={i18n.t('settings.work_schedule.title')}
        description={i18n.t('settings.work_schedule.settings_tab.description')}
      >
        <WorkScheduleForm
          onSubmit={() => (noOfEmployees && !openWarning ? setOpenWarning(true) : performAction())}
          periodFormRef={periodFormRef}
          dayConfigForms={dayConfigForms}
          totalHours={totalHours}
          onAddDayConfig={onAddDayConfig}
          onAddBreak={onAddBreak}
          onDeleteBreak={onDeleteBreak}
          onDeleteDayConfig={onDeleteDayConfig}
          onCopyFirstToAll={onCopyFirstToAll}
          disabled={running || !form.get('name').hasValue}
          openWarning={openWarning}
          onClose={() => setOpenWarning(false)}
          confirm={onConfirm}
          workSchedule={workSchedule}
          canEdit={canEdit}
          canReassignAll={canReassignAll}
          canAssignEmployees={canAssignEmployees}
          canAddBreaks={canAddBreaks}
        />
      </Section.TwoThirds>
    </>
  )
}

export default observer(EditWorkScheduleSection)
